import {
  ClientPersonFilterStatusEnum,
  type ClientPost
} from '@DigitaleDoerfer/digitale-doerfer-api';

import type { DorfHelferTypeEnum } from '@/features/posts/types/dorfHelferType';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { getTypedValue } from '@/shared/utils/object';
import { isEmptyOrNull } from '@/shared/utils/string';
import { dayjs } from '@/time';

export const getHappeningCardDateString = ({
  startTime,
  endTime
}: {
  startTime: number;
  endTime?: number;
}) => {
  const endDate = endTime ? dayjs(endTime) : undefined;
  const isSameDay = endDate?.isSame(dayjs(startTime), 'day') ?? true;
  const format = isSameDay ? 'dd, D. MMM' : 'D. MMM';
  // oxlint-disable-next-line unicorn/no-nested-ternary
  return `${dayjs(startTime).format(format)} ${isSameDay && endDate ? '' : endDate ? `- ${endDate.format(format)}` : ''}`;
};

export const getHappeningDateString = ({
  startTime,
  endTime,
  withYear = true,
  withTime = true
}: {
  startTime: number;
  endTime?: number;
  withYear?: boolean;
  withTime?: boolean;
}) => {
  const endDate = endTime ? dayjs(endTime) : undefined;
  const isSameDay = endDate?.isSame(dayjs(startTime), 'day') ?? true;
  const format = `dd, D. MMM${withYear ? ' YYYY' : ''}${withTime ? ', HH:mm' : ''}`;
  // oxlint-disable-next-line unicorn/no-nested-ternary
  return `${dayjs(startTime).format(format)} ${isSameDay && endDate ? (withTime ? `- ${endDate.format('HH:mm')}` : '') : endDate ? `- ${endDate.format(format)}` : ''}`;
};

export const getPostProps = (
  post?: ClientPost
): GenericPostDetail | undefined => {
  if (post === undefined) return undefined;
  else if (post?.gossip) {
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: `${post.gossip?.creator?.firstName} ${post.gossip?.creator?.lastName}`,
      creatorOrSitePicture: post.gossip?.creator?.profilePicture,
      isBlocked:
        post?.gossip?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.gossip?.commentsAllowed ?? true,
      ...post?.gossip
    };
  } else if (post?.happening) {
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: post.happening?.creator
        ? `${post.happening?.creator?.firstName} ${post.happening?.creator?.lastName}`
        : post.happening?.siteName,
      creatorOrSitePicture:
        post.happening?.creator?.profilePicture ?? post.happening?.sitePicture,
      isBlocked:
        post?.happening?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.happening?.commentsAllowed ?? true,
      ...post?.happening
    };
  } else if (post?.newsItem) {
    const katwarnEventCodeText = getTypedValue<string>(
      'katwarn.eventCodeText',
      post.newsItem?.customAttributes
    );
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: post.newsItem?.siteName,
      creatorOrSitePicture: post.newsItem?.sitePicture,
      katwarnEventCodeText,
      katwarnIncidentCategory: getTypedValue<string>(
        'katwarn.incidentCategory',
        post.newsItem?.customAttributes
      ),
      isBlocked:
        post?.newsItem?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.newsItem?.commentsAllowed ?? true,
      isCivilProtection: !isEmptyOrNull(katwarnEventCodeText),
      ...post?.newsItem
    };
  } else if (post?.offer) {
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: `${post.offer?.creator?.firstName} ${post.offer?.creator?.lastName}`,
      creatorOrSitePicture: post.offer?.creator?.profilePicture,
      isBlocked:
        post?.offer?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.offer?.commentsAllowed ?? true,
      ...post?.offer
    };
  } else if (post?.seeking) {
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: `${post.seeking?.creator?.firstName} ${post.seeking?.creator?.lastName}`,
      creatorOrSitePicture: post.seeking?.creator?.profilePicture,
      isBlocked:
        post?.seeking?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.seeking?.commentsAllowed ?? true,
      ...post?.seeking
    };
  } else if (post?.specialPost) {
    const dorfHelferType = getTypedValue<DorfHelferTypeEnum>(
      'dorfHelferType',
      post.specialPost?.customAttributes
    );
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: `${post.specialPost?.creator?.firstName} ${post.specialPost?.creator?.lastName}`,
      creatorOrSitePicture: post.specialPost?.creator?.profilePicture,
      isBlocked:
        post?.specialPost?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.specialPost?.commentsAllowed ?? true,
      dorfHelferType,
      ...post?.specialPost
    };
  } else if (post?.suggestion) {
    return {
      postId: post.id,
      postType: post?.type,
      creatorOrSiteName: `${post.suggestion?.creator?.firstName} ${post.suggestion?.creator?.lastName}`,
      creatorOrSitePicture: post.suggestion?.creator?.profilePicture,
      isBlocked:
        post?.suggestion?.creator?.filterStatus ===
        ClientPersonFilterStatusEnum.Blocked,
      commentsAllowed: post.suggestion?.commentsAllowed ?? true,
      ...post?.suggestion
    };
  } else {
    return undefined;
  }
};
